import styled, { css, keyframes } from 'styled-components';
import { v2Colors, v2FontFamily, v2Breakpoints } from '../../common/formInputsV2/styles/tokens';

// v2 look for the purchase pages' "documents" step (statuses 1, 2 and 5).
// Kept apart from Purchase.styles.js on purpose: the old styled components
// there are still used by the purchase pages that have not been migrated yet.
// The shared pieces (document tiles, uploader, SaveBox, resubmission warning)
// switch to these through an optional `v2` prop.

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

// Small spinner for buttons that are waiting on a request.
export const ButtonSpinner = styled.span`
  display: inline-block;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
`;

const noticeTones = {
  info: css`
    background: ${v2Colors.primarySoft};
    border-color: ${v2Colors.primaryBorder};
    color: #05603a;

    svg {
      color: ${v2Colors.primary};
    }
  `,
  warning: css`
    background: ${v2Colors.warningSoft};
    border-color: ${v2Colors.warningBorder};
    color: ${v2Colors.warningText};

    svg {
      color: ${v2Colors.warning};
    }
  `,
};

export const Notice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid transparent;
  border-radius: 12px;
  font-family: ${v2FontFamily};
  font-size: 14px;
  line-height: 1.5;
  ${({ $tone }) => noticeTones[$tone || 'info']}

  svg {
    flex-shrink: 0;
    margin-top: 3px;
    font-size: 16px;
  }

  p {
    margin: 0;
  }

  p + p {
    margin-top: 2px;
  }

  strong {
    font-weight: 700;
  }

  ol {
    margin: 6px 0 0;
    padding-left: 18px;
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    padding: 10px 12px;
    font-size: 13px;
  }
`;

// Numbered steps inside a Notice (e.g. the documents resubmission warning).
export const NoticeSteps = styled.ol`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 10px 0 0 !important;
  padding: 0 !important;
  list-style: none;
  counter-reset: notice-step;

  li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    counter-increment: notice-step;
  }

  li::before {
    content: counter(notice-step);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    margin-top: 1px;
    border-radius: 50%;
    background: ${v2Colors.warning};
    color: #fff;
    font-size: 11.5px;
    font-weight: 700;
    line-height: 1;
  }
`;

export const SaveQuoteButton = styled.button`
  align-self: flex-start;
  padding: 9px 18px;
  border: 1.5px solid ${v2Colors.primary};
  border-radius: 10px;
  background: #fff;
  font-family: ${v2FontFamily};
  font-size: 14px;
  font-weight: 600;
  color: ${v2Colors.primary};
  cursor: pointer;
  transition: background-color 150ms ease;

  &:hover,
  &:focus-visible {
    background: ${v2Colors.primarySoft};
  }
`;

// "Importante" declaration. Amber while it still has to be ticked, calm green
// once confirmed (`$done` comes from the checkbox's own checked value).
const confirmTones = {
  todo: css`
    background: ${v2Colors.warningSoft};
    border-color: ${v2Colors.warningBorder};
    border-left-color: ${v2Colors.warning};

    p {
      color: ${v2Colors.warningText};
    }

    input {
      accent-color: ${v2Colors.warning};
    }
  `,
  done: css`
    background: ${v2Colors.primarySoft};
    border-color: ${v2Colors.primaryBorder};
    border-left-color: ${v2Colors.primary};

    p {
      color: ${v2Colors.primaryHover};
    }

    input {
      accent-color: ${v2Colors.primary};
    }
  `,
};

export const ConfirmCard = styled.div`
  padding: 16px 18px;
  border: 1px solid transparent;
  border-left-width: 5px;
  border-radius: 12px;
  font-family: ${v2FontFamily};
  color: ${v2Colors.text};
  ${({ $done }) => ($done ? confirmTones.done : confirmTones.todo)}

  p {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0 0 8px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  label {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.45;
    cursor: pointer;
  }

  input {
    flex-shrink: 0;
    width: 22px;
    height: 22px;
    margin: 0;
    cursor: pointer;
  }

  input:disabled {
    cursor: default;
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    padding: 14px 16px;

    label {
      font-size: 14px;
    }
  }
`;

export const DocsCard = styled.div`
  padding: 16px 18px;
  background: ${v2Colors.inputBackground};
  border: 1px solid ${v2Colors.border};
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(16, 24, 40, 0.06);
  font-family: ${v2FontFamily};
  color: ${v2Colors.text};

  &.document-all-uploaded-border {
    border-color: ${v2Colors.primary};
    box-shadow: 0 0 0 1px ${v2Colors.primary}, 0 4px 16px rgba(16, 24, 40, 0.06);
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    padding: 14px 16px;
  }
`;

export const DocsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
`;

export const StepBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: ${v2Colors.primary};
  font-size: 13px;
  font-weight: 700;
  color: #fff;
`;

export const DocsTitle = styled.h2`
  margin: 0;
  font-family: ${v2FontFamily};
  font-size: 17px;
  font-weight: 600;
  line-height: 1.3;
  color: ${v2Colors.text};

  b {
    font-weight: 700;
    color: ${v2Colors.primary};
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    font-size: 15px;
  }
`;

export const DocsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
  gap: 12px;

  > * {
    min-width: 0;
  }
`;

export const DocsFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

export const SubmitDocsButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 42px;
  padding: 10px 22px;
  border: none;
  border-radius: 10px;
  background: ${v2Colors.primary};
  font-family: ${v2FontFamily};
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  transition: background-color 150ms ease;

  &:hover:not(:disabled),
  &:focus-visible {
    background: ${v2Colors.primaryHover};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    width: 100%;
  }
`;

// ---- document tiles (shared NonSignableDocument* components, v2 variant) ----

export const UploadedDocumentTile = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 160px;
  padding: 36px 12px 14px;
  background: ${v2Colors.inputBackground};
  border: 1px solid ${v2Colors.disabledBorder};
  border-radius: 14px;
  font-family: ${v2FontFamily};
  text-align: center;

  img {
    width: 34px;
    height: auto;
  }

  p {
    margin: 0;
    font-size: 12.5px;
    line-height: 1.35;
    color: ${v2Colors.textMuted};
    cursor: pointer;
  }

  p:not(.text-danger):hover {
    color: ${v2Colors.primaryHover};
    text-decoration: underline;
  }

  p.text-danger {
    cursor: default;
    color: ${v2Colors.danger} !important;
  }

  h3 {
    margin: 0;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
    line-height: 1.3;
    color: ${v2Colors.text};
    overflow-wrap: anywhere;
  }
`;

const badgeTones = {
  valid: css`
    background: ${v2Colors.primarySoft};
    border-color: ${v2Colors.primaryBorder};
    color: ${v2Colors.primaryHover};
  `,
  invalid: css`
    background: ${v2Colors.dangerSoft};
    border-color: ${v2Colors.dangerBorder};
    color: ${v2Colors.danger};
  `,
  pending: css`
    background: ${v2Colors.warningSoft};
    border-color: ${v2Colors.warningBorder};
    color: ${v2Colors.warningText};
  `,
};

export const StatusBadge = styled.span`
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 3px 10px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-family: ${v2FontFamily};
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.3;
  ${({ tone }) => badgeTones[tone || 'pending']}
`;

export const RemoveDocumentButton = styled.div`
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  font-size: 18px;
  cursor: pointer;
  transition: background-color 150ms ease;

  &:hover {
    background: ${v2Colors.dangerSoftHover};
  }
`;

export const OptionalTile = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 160px;
  padding: 36px 12px 14px;
  background: ${v2Colors.primarySoft};
  border: 1.5px dashed ${v2Colors.primaryBorder};
  border-radius: 14px;
  font-family: ${v2FontFamily};

  img {
    width: 34px;
    height: auto;
  }

  button {
    padding: 8px 14px;
    border: 1.5px solid ${v2Colors.primary};
    border-radius: 10px;
    background: #fff;
    font-family: ${v2FontFamily};
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    color: ${v2Colors.primary};
    text-align: center;
    cursor: pointer;
    transition: background-color 150ms ease;
  }

  button:hover,
  button:focus-visible {
    background: ${v2Colors.primarySoftHover};
  }
`;

export const OptionalBadge = styled.span`
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 3px 10px;
  border-radius: 999px;
  background: ${v2Colors.disabledSurface};
  font-family: ${v2FontFamily};
  font-size: 11.5px;
  font-weight: 600;
  line-height: 1.3;
  color: ${v2Colors.textMuted};
`;

// ---- status screens (6 emission pending, 8 sent to company, 9 KO) ----

const statusTones = {
  success: css`
    background: ${v2Colors.primarySoft};
    border-color: ${v2Colors.primaryBorder};

    .status-icon {
      background: ${v2Colors.primary};
    }

    h4 {
      color: ${v2Colors.primaryHover};
    }
  `,
  danger: css`
    background: ${v2Colors.dangerSoft};
    border-color: ${v2Colors.dangerBorder};

    .status-icon {
      background: ${v2Colors.danger};
    }

    h4 {
      color: ${v2Colors.danger};
    }
  `,
};

export const StatusCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  max-width: 760px;
  margin: 12px auto 0;
  padding: 20px 22px;
  border: 1px solid transparent;
  border-radius: 16px;
  font-family: ${v2FontFamily};
  color: ${v2Colors.text};
  ${({ $tone }) => statusTones[$tone || 'success']}

  .status-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    font-size: 17px;
    color: #fff;
  }

  h4 {
    margin: 0 0 6px;
    font-family: ${v2FontFamily};
    font-size: 18px;
    font-weight: 700;
    line-height: 1.3;
  }

  p {
    margin: 0;
    font-size: 14.5px;
    line-height: 1.55;
    color: ${v2Colors.textMuted};
  }

  p + p {
    margin-top: 6px;
  }

  p strong {
    font-weight: 700;
    color: ${v2Colors.text};
  }

  @media (max-width: ${v2Breakpoints.tablet}) {
    gap: 12px;
    padding: 16px;

    .status-icon {
      width: 34px;
      height: 34px;
      font-size: 14px;
    }

    h4 {
      font-size: 16px;
    }
  }
`;
